import { ReactElement, useState, useContext, MouseEvent } from "react";
import { useI18n } from "i18n";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Card } from "@remo-co/ui-core/src/components/Card";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Lottie } from "@remo-co/ui-core/src/components/Lottie";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { selectUserId } from "modules/auth/redux/selectors";
import { selectIsMobile } from "modules/deviceInfo";
import { useSelector } from "react-redux";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { isTrialCompleted } from "modules/companyPlanSettings/utils/trial";
import useStyles from "./BlankState.styles";
import EventDirectoryDialog from "./EventDirectoryDialog";
import AnimationData from "../assets/animation.lottie.json";
import { PurchaseAddonPopover } from "./PurchaseAddonPopover";

const BlankState = (): ReactElement => {
  const styles = useStyles();
  const { t } = useI18n(["eventDirectory"]);
  const { track } = useContext(TRACKING_CONTEXT);
  const userId = useSelector(selectUserId);
  const isMobileView = useSelector(selectIsMobile);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [showPurchaseAddonDialog, setShowPurchaseAddonDialog] = useState(false);
  const [buttonEl, setButtonEl] = useState<HTMLButtonElement | null>(null);
  const { myCompanyPlan } = useCompanyPlanSettings();
  const { getDirectoryEventLimit } = useCompanyPlanSettings();
  const maxEvents = getDirectoryEventLimit();

  const toggleDialog = () => {
    if (userId) {
      track(Events.LINK_EVENTS_CLICKED, {
        userId,
      });
    }
    setDialogOpen(!isDialogOpen);
  };

  const toggleSubscription = (e: MouseEvent<HTMLElement>) => {
    if (maxEvents !== 1) return;

    setShowPurchaseAddonDialog(!showPurchaseAddonDialog);
    setButtonEl(e.currentTarget as HTMLButtonElement);
  };

  const closePurchaseDialog = () => setShowPurchaseAddonDialog(false);

  return (
    <Card
      size="md"
      className={styles.card}
      innerClassName={styles.cardInner}
      flex
    >
      <Container size="xs">
        <Typography variant="h4" className={styles.title}>
          {t("eventDirectory:host.larger.events")}
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          className={styles.subtitle}
        >
          {t("eventDirectory:host.larger.events.subtitle")}
        </Typography>
        <Container
          className={styles.buttonWrapper}
          flex
          justifyContent="flex-start"
        >
          <Box data-testid="link-events-wrapper" onClick={toggleSubscription}>
            {isMobileView ? (
              <Button
                color="dark"
                size="md"
                variant="ghost"
                disabled
                className={styles.button}
                data-testid="try-on-desktop-btn"
              >
                {t("eventDirectory:try.on.desktop")}
              </Button>
            ) : (
              <Button
                color="dark"
                size="md"
                variant="secondary"
                className={styles.button}
                data-testid="link-events-btn"
                onClick={toggleDialog}
                disabled={isTrialCompleted(myCompanyPlan) || maxEvents === 1}
              >
                {t("eventDirectory:link.events")}
              </Button>
            )}
          </Box>
        </Container>
      </Container>
      <Container flex className={styles.animationContainer}>
        <Lottie animationData={AnimationData} className={styles.animationSvg} />
      </Container>

      {isDialogOpen && <EventDirectoryDialog handleClose={toggleDialog} />}
      {showPurchaseAddonDialog && (
        <PurchaseAddonPopover
          open={showPurchaseAddonDialog}
          handleClose={closePurchaseDialog}
          buttonEl={buttonEl}
        />
      )}
    </Card>
  );
};

export default BlankState;
